import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { UserPreference } from '../types/database';

export function useUserPreferences(userId: string, targetUserId: string) {
  const [preference, setPreference] = useState<UserPreference | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Don't try to load preferences if user is viewing their own profile
    if (userId === targetUserId) {
      setLoading(false);
      return;
    }
    
    loadPreference();
  }, [userId, targetUserId]);

  const loadPreference = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .eq('target_user_id', targetUserId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setPreference(data);
    } catch (err) {
      console.error('Error loading user preferences:', err);
      setError(err instanceof Error ? err : new Error('Failed to load user preferences'));
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (updates: Partial<UserPreference>) => {
    try {
      setError(null);

      const { error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: userId,
          target_user_id: targetUserId,
          ...updates
        });

      if (error) throw error;

      await loadPreference();
    } catch (err) {
      console.error('Error updating user preferences:', err);
      setError(err instanceof Error ? err : new Error('Failed to update user preferences'));
    }
  };

  const deletePreference = async () => {
    try {
      setError(null);

      const { error } = await supabase
        .from('user_preferences')
        .delete()
        .eq('user_id', userId)
        .eq('target_user_id', targetUserId);

      if (error) throw error;

      setPreference(null);
    } catch (err) {
      console.error('Error deleting user preferences:', err);
      setError(err instanceof Error ? err : new Error('Failed to delete user preferences'));
    }
  };

  return {
    preference,
    loading,
    error,
    updatePreference,
    deletePreference,
    refresh: loadPreference
  };
}